import type {
  AgentDashboard,
  AgentDashboardWithWidgets,
  AgentDashboardWidget,
  LookerWidgetData,
} from "@ligament/shared";
import { api } from "./client";

export interface WidgetValidationResult {
  dashboardId: string;
  dashboard: string;
  widgetId: string;
  title: string;
  explore: string | null;
  status: "ok" | "no_data" | "error" | "no_query" | "timeout";
  rows?: number;
  error?: string;
}

export interface WidgetValidationReport {
  checkedAt: string;
  totalWidgets: number;
  byStatus: Record<string, number>;
  problems: WidgetValidationResult[];
  results: WidgetValidationResult[];
  error?: string;
}

export const agentDashboardsApi = {
  list: (companyId: string, agentId?: string) => {
    const qs = agentId ? `?agentId=${agentId}` : "";
    return api.get<AgentDashboard[]>(`/companies/${companyId}/agent-dashboards${qs}`);
  },

  get: (id: string) => api.get<AgentDashboardWithWidgets>(`/agent-dashboards/${id}`),

  create: (companyId: string, data: Record<string, unknown>) =>
    api.post<AgentDashboard>(`/companies/${companyId}/agent-dashboards`, data),

  update: (id: string, data: Record<string, unknown>) =>
    api.patch<AgentDashboard>(`/agent-dashboards/${id}`, data),

  remove: (id: string) => api.delete<AgentDashboard>(`/agent-dashboards/${id}`),

  // Widget operations
  addWidget: (dashboardId: string, data: Record<string, unknown>) =>
    api.post<AgentDashboardWidget>(`/agent-dashboards/${dashboardId}/widgets`, data),

  updateWidget: (widgetId: string, data: Record<string, unknown>) =>
    api.patch<AgentDashboardWidget>(`/agent-dashboard-widgets/${widgetId}`, data),

  removeWidget: (widgetId: string) =>
    api.delete<AgentDashboardWidget>(`/agent-dashboard-widgets/${widgetId}`),

  // Auto-regenerate per-category dashboards from the approved metric catalog.
  regenerate: (companyId: string) =>
    api.post<{
      companyId: string;
      categoriesProcessed: string[];
      dashboardsCreated: number;
      dashboardsUpdated: number;
      widgetsWritten: number;
    }>(`/companies/${companyId}/agent-dashboards/regenerate`, {}),

  // Validate every widget by running its Looker query (ok / no_data / error).
  validateWidgets: (companyId: string) =>
    api.get<WidgetValidationReport>(`/companies/${companyId}/agent-dashboards/validate`),

  // Looker query proxy
  runQuery: (
    companyId: string,
    query: {
      model: string;
      explore: string;
      fields: string[];
      filters?: Record<string, string>;
      sorts?: string[];
      limit?: number;
    },
  ) => api.post<LookerWidgetData>(`/companies/${companyId}/looker-query`, query),
};
